import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { printReceipt as sendPrintReceipt } from '../../../../services/localPrintService';
import { migrosApi } from '../../../../config/apiClient';

interface MigrosOrderDetail {
  id: number;
  migrosOrderId: number;
  storeId: number;
  externalOrderId?: number;
  shortCode?: string;
  status?: string;
  deliveryProvider?: string;
  customerFullName?: string;
  customerPhoneNumber?: string;
  addressDetail?: string;
  districtName?: string;
  townName?: string;
  cityName?: string;
  latitude?: number;
  longitude?: number;
  totalPrice: number;
  discountedPrice?: number;
  paymentType?: string;
  isOnlinePayment: boolean;
  orderNote?: string;
  contactlessDelivery: boolean;
  ringDoorBell: boolean;
  saveGreen: boolean;
  companyId?: string;
  branchId?: string;
  assignedCourierId?: number;
  assignedCourierName?: string;
  createdAt: string;
  updatedAt: string;
  items?: MigrosOrderItemDetail[];
}

interface MigrosOrderItemDetail {
  id: number;
  name: string;
  amount: number;
  price: number;
  unitPrice: number;
  note?: string;
  options?: MigrosOrderOptionDetail[];
}

interface MigrosOrderOptionDetail {
  id: number;
  objectOptionItemId: number;
  optionItemId: number;
  productId?: number;
  parentObjectOptionItemId: number;
  headerName: string;
  itemNames: string;
  primaryPrice: number;
  primaryDiscountedPrice: number;
  quantity: number;
  excluded: boolean;
  optionType: string;
}

interface ProcessedOption {
  headerName: string;
  itemNames: string;
  quantity: number;
  priceTL: number;
  excluded: boolean;
  optionType: string;
  subOptions: ProcessedSubOption[];
}

interface ProcessedSubOption {
  headerName: string;
  itemNames: string;
  quantity: number;
  priceTL: number;
  excluded: boolean;
}

export interface MigrosOrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number | null;
  companyId: string;
  /**
   * Online ödenmiş siparişler için Pavo cihazından mali fiş kestirme handler'i.
   * Tanimliysa modaldaki "Online Fis Yazdir" butonu aktif olur (sadece online
   * odenmis siparislerde gorunur).
   */
  onRequestOnlineReceipt?: () => void;
  screenData?: {
    id: number;
    localServiceUrl?: string;
    printingSettings?: {
      printingMode: string;
      defaultReceiptPrinter?: { id: string; name: string } | null;
      adisyonPrinter?: { id: string; name: string } | null;
      receiptDisplaySettings?: {
        companyName?: string;
        companyAddress?: string;
        taxNumber?: string;
        headerType?: 'text' | 'logo';
        logoUrl?: string;
        logoMaxWidthPx?: number;
        logoMaxHeightPx?: number;
        standardFooterNote?: string;
        thankYouMessage?: string;
      };
    };
  };
}

const getStatusLabel = (status?: string): { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' } => {
  const s = (status || '').toUpperCase();
  if (s === 'NEW_PENDING' || s === 'PENDING') return { label: 'Onay Bekleniyor', color: 'warning' };
  if (s === 'APPROVED') return { label: 'Onaylandi', color: 'info' };
  if (s === 'PREPARED') return { label: 'Hazirlandi', color: 'success' };
  if (s === 'DELIVERY') return { label: 'Yolda', color: 'info' };
  if (s === 'COMPLETED') return { label: 'Teslim Edildi', color: 'success' };
  if (s === 'REJECTED') return { label: 'Reddedildi', color: 'error' };
  if (s === 'CANCELLED') return { label: 'Iptal Edildi', color: 'error' };
  return { label: status || 'Bilinmiyor', color: 'default' };
};

const toTurkeyTime = (dateStr?: string): Date | null => {
  if (!dateStr) return null;
  const utcStr = (!dateStr.endsWith('Z') && !dateStr.includes('+')) ? dateStr + 'Z' : dateStr;
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getTime() + 3 * 60 * 60 * 1000);
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const d = toTurkeyTime(dateStr);
    if (!d) return '-';
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    const h = String(d.getUTCHours()).padStart(2, '0');
    const m = String(d.getUTCMinutes()).padStart(2, '0');
    const s = String(d.getUTCSeconds()).padStart(2, '0');
    return `${day}.${month}.${year} ${h}:${m}:${s}`;
  } catch { return dateStr; }
};

const formatTime = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const d = toTurkeyTime(dateStr);
    if (!d) return '-';
    const h = String(d.getUTCHours()).padStart(2, '0');
    const m = String(d.getUTCMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  } catch { return '-'; }
};

const processItemOptions = (options?: MigrosOrderOptionDetail[]): ProcessedOption[] => {
  if (!options || options.length === 0) return [];
  const topLevel = options.filter(o => o.parentObjectOptionItemId === -1);
  const subMap = new Map<number, MigrosOrderOptionDetail[]>();
  options.filter(o => o.parentObjectOptionItemId !== -1).forEach(sub => {
    const key = sub.parentObjectOptionItemId;
    if (!subMap.has(key)) subMap.set(key, []);
    subMap.get(key)!.push(sub);
  });
  return topLevel.map(opt => ({
    headerName: opt.headerName,
    itemNames: opt.itemNames,
    quantity: opt.quantity,
    priceTL: (opt.primaryDiscountedPrice || opt.primaryPrice || 0) / 100,
    excluded: opt.excluded,
    optionType: opt.optionType,
    subOptions: (subMap.get(opt.objectOptionItemId) || []).map(sub => ({
      headerName: sub.headerName,
      itemNames: sub.itemNames,
      quantity: sub.quantity,
      priceTL: (sub.primaryDiscountedPrice || sub.primaryPrice || 0) / 100,
      excluded: sub.excluded,
    })),
  }));
};

const MigrosOrderDetailModal: React.FC<MigrosOrderDetailModalProps> = ({
  open,
  onClose,
  orderId,
  companyId,
  screenData,
  onRequestOnlineReceipt,
}) => {
  const [order, setOrder] = useState<MigrosOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (open && orderId && companyId) {
      const fetchOrderDetail = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await migrosApi.get(`/api/migros-orders/order/${orderId}`);
          const result = response.data;
          if (result.success && result.data) {
            setOrder(result.data);
          } else {
            setError(result.message || 'Siparis detayi yuklenemedi');
          }
        } catch (err) {
          setError('Siparis detayi yuklenirken hata olustu');
          console.error('[MigrosDetail] Yukleme hatasi:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchOrderDetail();
    } else {
      setOrder(null);
    }
  }, [open, orderId, companyId]);

  const createMigrosReceiptHtml = useCallback((o: MigrosOrderDetail): string => {
    const receiptSettings = screenData?.printingSettings?.receiptDisplaySettings;
    const items = o.items || [];
    const turkeyDate = toTurkeyTime(o.createdAt);
    const dateStr = turkeyDate
      ? `${String(turkeyDate.getUTCDate()).padStart(2,'0')}.${String(turkeyDate.getUTCMonth()+1).padStart(2,'0')}.${turkeyDate.getUTCFullYear()}`
      : new Date().toLocaleDateString('tr-TR');
    const timeStr = turkeyDate
      ? `${String(turkeyDate.getUTCHours()).padStart(2,'0')}:${String(turkeyDate.getUTCMinutes()).padStart(2,'0')}`
      : '';
    const totalTL = (o.discountedPrice || o.totalPrice || 0) / 100;
    const originalTL = (o.totalPrice || 0) / 100;
    const hasDiscount = o.discountedPrice != null && o.discountedPrice !== o.totalPrice;
    const deliveryLabel = o.deliveryProvider === 'MIGROS' ? 'Migros Kurye' : 'Restoran Kuryesi';
    const paymentLabel = o.isOnlinePayment ? 'Online Odeme' : (o.paymentType || 'Kapida Odeme');

    const renderReceiptOptions = (options?: MigrosOrderOptionDetail[]): string => {
      const processed = processItemOptions(options);
      if (processed.length === 0) return '';
      let html = '';
      const excludedTop = processed.filter(o => o.excluded);
      const includedTop = processed.filter(o => !o.excluded);
      if (excludedTop.length > 0) {
        const grouped = new Map<string, string[]>();
        excludedTop.forEach(e => {
          const key = e.headerName || 'Malzemeler';
          if (!grouped.has(key)) grouped.set(key, []);
          grouped.get(key)!.push(e.itemNames);
        });
        grouped.forEach((items, header) => {
          html += `<tr class="option-row"><td colspan="4" style="color:#c00;">X CIKARILDI (${header}): ${items.join(', ')}</td></tr>`;
        });
      }
      includedTop.forEach(opt => {
        const priceStr = opt.priceTL > 0 ? ` +${opt.priceTL.toFixed(2)} TL` : '';
        const qtyStr = opt.quantity > 1 ? ` x${opt.quantity}` : '';
        html += `<tr class="option-row"><td colspan="4">+ ${opt.headerName}: ${opt.itemNames}${qtyStr}${priceStr}</td></tr>`;
        const excludedSubs = opt.subOptions.filter(s => s.excluded);
        const includedSubs = opt.subOptions.filter(s => !s.excluded);
        if (excludedSubs.length > 0) {
          const subGrouped = new Map<string, string[]>();
          excludedSubs.forEach(s => {
            const key = s.headerName || 'Malzemeler';
            if (!subGrouped.has(key)) subGrouped.set(key, []);
            subGrouped.get(key)!.push(s.itemNames);
          });
          subGrouped.forEach((items, header) => {
            html += `<tr class="option-row"><td colspan="4" style="padding-left:16px;color:#c00;">X ${header}: ${items.join(', ')}</td></tr>`;
          });
        }
        includedSubs.forEach(sub => {
          const sp = sub.priceTL > 0 ? ` +${sub.priceTL.toFixed(2)} TL` : '';
          const sq = sub.quantity > 1 ? ` x${sub.quantity}` : '';
          html += `<tr class="option-row"><td colspan="4" style="padding-left:16px;">  > ${sub.headerName}: ${sub.itemNames}${sq}${sp}</td></tr>`;
        });
      });
      return html;
    };

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Migros Siparis Fisi</title>
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Poppins', sans-serif; font-size: 9pt; width: 72mm; max-width: 72mm; margin: 0; padding: 1mm 2mm 2mm 2mm; line-height: 1.2; color: #000; }
      .header { text-align: center; margin-bottom: 3px; }
      .header h2 { margin: 0 0 2px 0; }
      .header img { max-width: 50mm; max-height: 18mm; }
      .migros-badge { text-align: center; margin: 5px 0; padding: 4px 0; font-weight: bold; font-size: 12pt; letter-spacing: 3px; border-top: 2px solid #000; border-bottom: 2px solid #000; }
      .order-number { font-size: 14pt; text-align: center; margin: 5px 0 8px 0; font-weight: bold; letter-spacing: 2px; }
      .customer-info { font-size: 10pt; margin: 5px 0; padding: 4px; border: 1px solid #000; }
      .customer-info p { margin: 2px 0; }
      .customer-name { font-weight: bold; font-size: 11pt; }
      .customer-address { font-size: 9pt; margin-top: 3px; padding: 3px; border: 1px dashed #000; }
      .invoice-details { margin-bottom: 3px; font-size: 8pt; }
      .invoice-details p { margin: 0 0 1px 0; }
      .items-table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 2px; table-layout: fixed; }
      .items-table th, .items-table td { text-align: left; padding: 1px 0; }
      .items-table th { border-bottom: 1px solid #000; }
      .option-row td { font-size: 7pt; padding-left: 8px; }
      .summary { margin-top: 5px; text-align: right; font-size: 9pt; }
      .summary p { margin: 0 0 2px 0; }
      .grand-total { font-size: 13pt; font-weight: bold; text-align: center; margin: 8px 0; padding: 5px 0; }
      .order-note { font-size: 8pt; margin: 5px 0; padding: 3px; border: 1px dashed #000; }
      .delivery-type { text-align: center; font-size: 9pt; font-weight: bold; padding: 3px 0; margin: 4px 0; border-bottom: 1px dashed #000; }
      .payment-info { text-align: center; font-size: 10pt; font-weight: bold; padding: 4px 0; margin: 5px 0; border: 1px solid #000; }
      .receipt-label { font-size: 10pt; text-align: center; margin: 8px 0; font-weight: bold; border: 1px solid #000; padding: 3px; letter-spacing: 2px; }
      .footer { margin-top: 5px; text-align: center; font-size: 8pt; border-top: 1px dashed #000; padding-top: 3px; }
      .right { text-align: right; }
      .bold { font-weight: bold; }
    </style></head><body>
      <div class="header">
        ${receiptSettings?.headerType === 'logo' && receiptSettings?.logoUrl
          ? `<img src="${receiptSettings.logoUrl}" alt="Logo" />`
          : `<h2>${receiptSettings?.companyName || 'AQUASOFT'}</h2>`}
      </div>
      ${receiptSettings?.headerType === 'logo' && receiptSettings?.logoUrl ? '' : `
        <div style="text-align:center;font-size:8pt;">
          ${receiptSettings?.companyAddress ? `<p style="margin:0 0 1px 0">${receiptSettings.companyAddress}</p>` : ''}
          ${receiptSettings?.taxNumber ? `<p style="margin:0 0 1px 0">VKN: ${receiptSettings.taxNumber}</p>` : ''}
        </div>
      `}
      <div class="migros-badge">* MIGROS SIPARISI *</div>
      <div class="order-number">SIPARIS: ${o.shortCode || o.migrosOrderId || o.id}</div>
      <div class="customer-info">
        <p class="customer-name">${o.customerFullName || 'Musteri'}</p>
        ${o.customerPhoneNumber ? `<p>Tel: ${o.customerPhoneNumber}</p>` : ''}
        ${o.addressDetail ? `<div class="customer-address">${[o.addressDetail, o.districtName, o.townName, o.cityName].filter(Boolean).join(', ')}</div>` : ''}
      </div>
      <div class="invoice-details">
        <p><span class="bold">Tarih:</span> ${dateStr} ${timeStr}</p>
        <p><span class="bold">Odeme:</span> ${paymentLabel}</p>
      </div>
      <div class="delivery-type">${deliveryLabel}</div>
      ${o.orderNote ? `<div class="order-note">${o.orderNote}</div>` : ''}
      ${o.contactlessDelivery ? `<div class="order-note">* Temassiz Teslimat *</div>` : ''}
      ${o.ringDoorBell === false ? `<div class="order-note" style="font-weight:bold;">* KAPI ZILINI CALMA *</div>` : ''}
      ${o.saveGreen ? `<div class="order-note">* Cevreci Paketleme *</div>` : ''}
      <table class="items-table">
        <thead><tr>
          <th style="width:45%;">Urun</th>
          <th class="right" style="width:12%;">Adet</th>
          <th class="right" style="width:20%;">Fiyat</th>
          <th class="right" style="width:23%;">Tutar</th>
        </tr></thead>
        <tbody>
          ${items.map(item => {
            const itemTotalTL = (item.price || 0) / 100;
            const itemUnitTL = (item.unitPrice || 0) / 100;
            let rows = `<tr>
              <td>${item.name}</td>
              <td class="right">${item.amount}</td>
              <td class="right">${itemUnitTL.toFixed(2)}</td>
              <td class="right">${itemTotalTL.toFixed(2)}</td>
            </tr>`;
            rows += renderReceiptOptions(item.options);
            if (item.note) {
              rows += `<tr class="option-row"><td colspan="4"><b>Not:</b> ${item.note}</td></tr>`;
            }
            return rows;
          }).join('')}
        </tbody>
      </table>
      <div class="summary">
        <p>Ara Toplam: ${originalTL.toFixed(2)} TL</p>
        ${hasDiscount ? `<p class="bold">Indirim: -${(originalTL - totalTL).toFixed(2)} TL</p>` : ''}
      </div>
      <div class="grand-total">TOPLAM: ${totalTL.toFixed(2)} TL</div>
      ${o.isOnlinePayment ? '<div class="payment-info">** ONLINE ODENDI **</div>' : ''}
      <div class="receipt-label">MIGROS PAKET SIPARIS FISI</div>
      <div class="footer">
        ${receiptSettings?.standardFooterNote ? `<p>${receiptSettings.standardFooterNote}</p>` : ''}
        ${receiptSettings?.thankYouMessage ? `<p>${receiptSettings.thankYouMessage}</p>` : ''}
      </div>
    </body></html>`;
  }, [screenData]);

  const handlePrintReceipt = useCallback(async () => {
    if (!order) return;
    const printer = screenData?.printingSettings?.defaultReceiptPrinter
      || screenData?.printingSettings?.adisyonPrinter;
    if (!printer) { alert('Fis yazicisi tanimlanmamis.'); return; }

    setIsPrinting(true);
    try {
      const localServiceUrl = screenData?.localServiceUrl || 'http://localhost:3003';
      const totalTL = (order.discountedPrice || order.totalPrice || 0) / 100;
      const originalTL = (order.totalPrice || 0) / 100;
      const rs = screenData?.printingSettings?.receiptDisplaySettings;
      const deliveryLabel = order.deliveryProvider === 'MIGROS' ? 'Migros Kurye' : 'Restoran Kuryesi';
      const addrFull = [order.addressDetail, order.districtName, order.townName, order.cityName].filter(Boolean).join(', ');
      const receiptData = {
        items: (order.items || []).map(it => {
          const subs: { name: string }[] = it.options ? (Array.isArray(it.options) ? it.options : []).map((opt: any) => ({ name: `+ ${opt.headerName || opt.optionName || ''}: ${opt.itemNames || opt.name || ''}` })) : [];
          if (it.note) subs.push({ name: `Not: ${it.note}` });
          return {
            name: it.name,
            quantity: it.amount,
            unitPrice: (it.unitPrice || it.price || 0) / 100,
            totalPrice: (it.price || 0) / 100,
            subItems: subs.length > 0 ? subs : undefined,
          };
        }),
        orderNumber: order.shortCode || String(order.migrosOrderId || order.id),
        customerName: order.customerFullName || 'Müşteri',
        customerPhone: order.customerPhoneNumber || '',
        deliveryAddress: addrFull,
        invoiceNumber: order.shortCode || String(order.id),
        date: order.createdAt ? new Date(order.createdAt).toLocaleDateString('tr-TR') : '',
        time: order.createdAt ? new Date(order.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '',
        grandTotal: totalTL,
        subtotal: originalTL,
        discountTotal: originalTL - totalTL,
        paymentMethod: order.paymentType || (order.isOnlinePayment ? 'Online Ödeme' : 'Kapıda Ödeme'),
        isOnlinePaid: order.isOnlinePayment,
        receiptLabel: 'MIGROS PAKET SIPARIS',
        deliveryType: deliveryLabel,
        customerNote: [order.orderNote, order.contactlessDelivery ? 'Temassiz Teslimat' : null, order.ringDoorBell === false ? 'Kapi zilini calma' : null, order.saveGreen ? 'Cevreci Paketleme' : null].filter(Boolean).join(' | ') || undefined,
        companyInfo: rs ? { name: rs.companyName || '', address: rs.companyAddress || '', taxNumber: rs.taxNumber || '' } : undefined,
        headerType: rs?.headerType,
        logoUrl: rs?.logoUrl,
        logoMaxWidthPx: rs?.logoMaxWidthPx ?? 384,
        logoMaxHeightPx: rs?.logoMaxHeightPx ?? 150,
        showReceiptHeader: true,
        standardFooterNote: rs?.standardFooterNote,
        thankYouMessage: rs?.thankYouMessage
      };
      await sendPrintReceipt({
        printerId: printer.id,
        printerName: printer.name,
        receiptData
      }, { baseUrl: localServiceUrl });
    } catch (err: any) {
      console.error('[MigrosDetail] Fis yazdirma hatasi:', err);
      alert(`Fis yazdirma hatasi: ${err.message || 'Bilinmeyen hata'}`);
    } finally {
      setIsPrinting(false);
    }
  }, [order, screenData]);

  const statusInfo = getStatusLabel(order?.status);
  const isMigrosCourier = order?.deliveryProvider === 'MIGROS';
  const totalTL = order ? (order.discountedPrice || order.totalPrice || 0) / 100 : 0;
  const originalTL = order ? (order.totalPrice || 0) / 100 : 0;
  const hasDiscount = order && order.discountedPrice != null && order.discountedPrice !== order.totalPrice;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#FF6000', color: 'white', py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShoppingCartIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">Migros Siparis Detayi</Typography>
            {order && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                #{order.shortCode || order.migrosOrderId}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#FF6000' }} />
          </Box>
        )}
        {error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error" variant="h6">{error}</Typography>
          </Box>
        )}

        {order && !loading && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">DURUM</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                </Box>
              </Paper>
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">SIPARIS ZAMANI</Typography>
                <Typography variant="h6" fontWeight="bold">{formatTime(order.createdAt)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {order.createdAt ? formatDate(order.createdAt).split(' ')[0] : '-'}
                </Typography>
              </Paper>
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160, bgcolor: isMigrosCourier ? '#E3F2FD' : '#FFF3E0' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">TESLIMAT TIPI</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {isMigrosCourier ? <LocalShippingIcon color="info" /> : <StorefrontIcon color="warning" />}
                  <Typography variant="body1" fontWeight="bold">
                    {isMigrosCourier ? 'Migros Kurye' : 'Restoran Kuryesi'}
                  </Typography>
                </Box>
              </Paper>
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">ODEME</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {order.isOnlinePayment ? 'Online Odeme' : (order.paymentType || 'Kapida Odeme')}
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Musteri Bilgileri</Typography>
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Ad Soyad</Typography>
                  <Typography variant="body1" fontWeight="bold">{order.customerFullName || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Telefon</Typography>
                  <Typography variant="body1">{order.customerPhoneNumber || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Adres</Typography>
                  <Typography variant="body1">
                    {[order.addressDetail, order.districtName, order.townName, order.cityName].filter(Boolean).join(', ') || '-'}
                  </Typography>
                </Grid>
              </Grid>
              {(order.orderNote || order.contactlessDelivery || order.ringDoorBell === false || order.saveGreen) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {order.contactlessDelivery && <Chip label="Temassiz Teslimat" size="small" color="info" variant="outlined" />}
                    {order.ringDoorBell === false && <Chip label="Kapi Zilini Calma" size="small" color="error" variant="outlined" />}
                    {order.ringDoorBell === true && <Chip label="Kapi Zilini Cal" size="small" color="success" variant="outlined" />}
                    {order.saveGreen && <Chip label="Cevreci Paketleme" size="small" color="success" variant="outlined" />}
                  </Box>
                  {order.orderNote && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                      <Typography variant="body2" color="warning.dark" fontWeight="medium">
                        Siparis Notu: {order.orderNote}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Siparis Kalemleri ({order.items?.length || 0} urun)
            </Typography>
            <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 2 }}>
              {order.items?.map((item, index) => {
                const itemTotalTL = (item.price || 0) / 100;
                const itemUnitTL = (item.unitPrice || 0) / 100;
                const processed = processItemOptions(item.options);
                const excludedOpts = processed.filter(o => o.excluded);
                const includedOpts = processed.filter(o => !o.excluded);
                return (
                  <Paper key={item.id || index} elevation={1} sx={{ p: 2, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="bold">{item.name}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                        <Typography variant="body2" color="text.secondary">
                          {item.amount} x {itemUnitTL.toFixed(2)} TL
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                          {itemTotalTL.toFixed(2)} TL
                        </Typography>
                      </Box>
                    </Box>
                    {item.note && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Typography variant="caption" color="warning.dark" fontWeight="medium">
                          Not: {item.note}
                        </Typography>
                      </Box>
                    )}
                    {excludedOpts.length > 0 && (() => {
                      const grouped = new Map<string, string[]>();
                      excludedOpts.forEach(e => {
                        const key = e.headerName || 'Malzemeler';
                        if (!grouped.has(key)) grouped.set(key, []);
                        grouped.get(key)!.push(e.itemNames);
                      });
                      return (
                        <Box sx={{ mt: 1 }}>
                          {Array.from(grouped.entries()).map(([header, names], gi) => (
                            <Box key={gi} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Typography variant="caption" color="error.main" fontWeight="bold">
                                ✕ Cikarilan ({header}):
                              </Typography>
                              <Typography variant="caption" color="error.main">
                                {names.join(', ')}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}
                    {includedOpts.length > 0 && (
                      <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                        {includedOpts.map((opt, oi) => (
                          <Box key={oi} sx={{ mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                {opt.headerName}:
                              </Typography>
                              <Typography variant="caption" color="text.primary">
                                {opt.itemNames}{opt.quantity > 1 ? ` x${opt.quantity}` : ''}
                              </Typography>
                              {opt.priceTL > 0 && (
                                <Typography variant="caption" color="success.main" fontWeight="bold">
                                  +{opt.priceTL.toFixed(2)} TL
                                </Typography>
                              )}
                            </Box>
                            {opt.subOptions.length > 0 && (
                              <Box sx={{ pl: 2 }}>
                                {(() => {
                                  const exSubs = opt.subOptions.filter(s => s.excluded);
                                  const inSubs = opt.subOptions.filter(s => !s.excluded);
                                  return (
                                    <>
                                      {exSubs.length > 0 && (() => {
                                        const subGrouped = new Map<string, string[]>();
                                        exSubs.forEach(s => {
                                          const k = s.headerName || 'Malzemeler';
                                          if (!subGrouped.has(k)) subGrouped.set(k, []);
                                          subGrouped.get(k)!.push(s.itemNames);
                                        });
                                        return Array.from(subGrouped.entries()).map(([h, n], si) => (
                                          <Typography key={si} variant="caption" color="error.main" component="div">
                                            ✕ {h}: {n.join(', ')}
                                          </Typography>
                                        ));
                                      })()}
                                      {inSubs.map((sub, si) => (
                                        <Typography key={si} variant="caption" color="text.secondary" component="div">
                                          → {sub.headerName}: {sub.itemNames}{sub.quantity > 1 ? ` x${sub.quantity}` : ''}
                                          {sub.priceTL > 0 ? ` +${sub.priceTL.toFixed(2)} TL` : ''}
                                        </Typography>
                                      ))}
                                    </>
                                  );
                                })()}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Urunler Toplami</Typography>
                <Typography variant="body1" fontWeight="bold">{originalTL.toFixed(2)} TL</Typography>
              </Box>
              {hasDiscount && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" color="error.main">Indirim</Typography>
                    <Typography variant="body1" color="error.main" fontWeight="bold">
                      -{(originalTL - totalTL).toFixed(2)} TL
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: hasDiscount ? 0 : 1, borderTop: hasDiscount ? 'none' : '2px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight="bold">TOPLAM</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {totalTL.toFixed(2)} TL
                </Typography>
              </Box>
            </Paper>

            {order.assignedCourierName && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Kurye Bilgileri</Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                  <Typography variant="body1" fontWeight="bold">{order.assignedCourierName}</Typography>
                </Paper>
              </>
            )}

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Zaman Cizelgesi</Typography>
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Olusturulma</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatDate(order.createdAt)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Son Guncelleme</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatDate(order.updatedAt)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
            onClick={handlePrintReceipt}
            disabled={!order || isPrinting}
            sx={{ bgcolor: '#FF6000', '&:hover': { bgcolor: '#CC4D00' } }}
          >
            {isPrinting ? 'Yazdırılıyor...' : 'Fiş Yazdır'}
          </Button>
          {/* Online ödenmiş Migros siparisleri icin REST-P2P uzerinden Pavo
              cihazindan mali fis kestirme. Sadece isOnlinePayment=true olan
              siparislerde gorunur (kapida odeme siparislerinde gizli). */}
          {onRequestOnlineReceipt && order && order.isOnlinePayment && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ReceiptLongIcon />}
              onClick={onRequestOnlineReceipt}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Online Fiş Yazdır
            </Button>
          )}
        </Box>
        <Button variant="outlined" onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MigrosOrderDetailModal;
